import { Link } from "react-router-dom";
import { ArrowRight, Handshake, Building2, UserPlus, Share2, Repeat, Globe2, ShieldCheck, Fingerprint, BadgeCheck, Globe } from "lucide-react";
import { COMPANY } from "../data/site";
import Reveal from "../components/common/Reveal";
import PartnerApplicationForm from "../components/forms/PartnerApplicationForm";
import useSeo from "../hooks/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const TYPES = [
  { icon: Handshake, title: "Split-Fee Recruitment", tag: "Most common", body: "Share candidates and vacancies and agree the commercial split before progressing." },
  { icon: Building2, title: "Agency Partnership", body: "Build ongoing recruitment relationships across markets and specialist sectors." },
  { icon: UserPlus, title: "Independent Recruiter Partnership", body: "Extend your capability without the cost of building a larger internal team." },
  { icon: Share2, title: "Referral Partnership", body: "Refer qualified candidates, clients, or opportunities through agreed terms." },
  { icon: Repeat, title: "Candidate Exchange", body: "Connect suitable talent with trusted recruitment partners." },
  { icon: Globe2, title: "International Recruitment Partnership", body: "Collaborate across borders through a structured recruitment partnership." },
];

const STEPS = [
  { t: "Register and get verified", b: "Tell us your business details. Every partner is reviewed — company registration, identity, and professional standing — before they can share or see anything in the network." },
  { t: "Verify", b: "While your application is reviewed, your account sits at Pending Verification. Once your business, identity, and professional standing are checked, you become a Verified Partner and can share and view network activity." },
  { t: "Share with Network", b: "Add the candidates or vacancies you want the network to see, and choose to Share with Network. Nothing goes out unless you choose it — shared records stay anonymised until both sides accept.", note: "Direct ATS connections (Bullhorn, JobAdder, Vincere, and others) are on our roadmap — manual sharing is always available." },
  { t: "Get matched", b: "Intelligent Matching surfaces your shared roles and candidates against every other partner's inventory — by skill, location, salary band, availability, and seniority." },
  { t: "Accept and agree the split", b: "Request collaboration on a match. Once both sides accept, agree the commercial split inside the Deal Room before anything else moves forward — this is locked in before identities are revealed." },
  { t: "Place", b: "Identities are revealed inside the private Deal Room, and you work the placement together through to a confirmed hire." },
  { t: "Settle", b: "The platform records the 10% platform fee and the 90% partner pool, split exactly as you and your partner agreed in Step 5." },
];

const CHECKS = [
  { icon: ShieldCheck, t: "Business verification", b: "Your company registration is checked against the relevant national registry for your business type and country." },
  { icon: Fingerprint, t: "Identity verification", b: "The named individual on the account is verified through an identity-verification check." },
  { icon: BadgeCheck, t: "Professional standing", b: "Where a recruitment body applies in your country, we check for membership; otherwise we review the professional profile and evidence you provide." },
  { icon: Globe, t: "Website check", b: "We confirm your website is live and reasonably matches the business you have registered." },
];

const FAQ = [
  { q: "Who can join?", a: "Limited companies, sole recruiters, and partnerships, across sectors, markets, and geographies — subject to passing verification." },
  { q: "Is the 90% split fixed?", a: "No. The 90% partner pool is negotiated directly between the two collaborating partners for each placement, and is agreed and locked in the Deal Room before identities are revealed." },
  { q: "Do I need to connect my ATS to join?", a: "No. Manual sharing is fully supported today. Direct ATS connections are on our roadmap." },
  { q: "Can other partners see my candidate's identity right away?", a: "No. Records are anonymised — profession, seniority, region, and rate/salary band only — until both sides accept a collaboration and agree the split." },
];

const SectionHead = ({ title, sub, light }) => (
  <div className="mx-auto max-w-2xl text-center">
    <h2 className={`font-serif text-3xl tracking-tight sm:text-4xl ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    {sub && <p className={`mt-4 text-base ${light ? "text-white/70" : "text-muted-foreground"}`}>{sub}</p>}
  </div>
);

export default function PartnershipCenter() {
  useSeo({ title: "Recruitment Partnership Center", description: "Connect with recruitment professionals across markets, share candidates and vacancies, and collaborate on placements through a secure, transparent partnership network." });
  return (
    <div className="bg-background text-charcoal" data-testid="partnership-center">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-navy/95 backdrop-blur" data-testid="pc-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-8">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY.logo} alt={COMPANY.name} className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20" />
            <span className="leading-tight"><span className="block font-serif text-lg font-bold text-white">Azimi &amp; Co.</span><span className="block text-xs font-light tracking-wide text-white/70">Recruitment Partnership Center</span></span>
          </Link>
          <a href="#register" className="rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="pc-nav-cta">Become a Recruitment Partner</a>
        </div>
      </header>

      <section className="bg-navy pt-32 pb-20 lg:pt-40 lg:pb-24" data-testid="pc-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Reveal>
            <span className="overline text-xs text-gold-light">Recruitment Partnership Center</span>
            <h1 className="mt-6 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">Expand your reach.<br />Place more talent.<br /><span className="text-gold">Build stronger partnerships.</span></h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75">Connect with recruitment professionals across markets, share candidates and vacancies, and collaborate on placements through a secure, transparent partnership network — open to recruitment businesses across sectors, markets and geographies.</p>
            <div className="mt-9 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <a href="#register" className="inline-flex items-center gap-2 rounded-lg bg-gold px-7 py-3.5 text-sm font-semibold text-white hover:bg-gold-dark" data-testid="pc-hero-cta">Become a Recruitment Partner <ArrowRight className="h-4 w-4" /></a>
              <a href="#how-it-works" className="text-sm font-semibold text-white/80 hover:text-gold" data-testid="pc-hero-secondary">See how it works</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white py-10" data-testid="pc-fee-bar">
        <div className="mx-auto grid max-w-7xl items-center gap-6 px-6 lg:grid-cols-[260px_1fr] lg:px-8">
          <p className="font-serif text-lg text-navy">What each successful placement pays out</p>
          <div>
            <div className="flex h-12 overflow-hidden rounded-lg">
              <div className="flex w-[10%] items-center justify-center bg-navy text-sm font-bold text-white">10%</div>
              <div className="flex flex-1 items-center justify-center bg-gold text-sm font-bold text-white">90%</div>
            </div>
            <div className="mt-2 flex text-xs text-muted-foreground"><span className="w-[10%] min-w-[90px]">Platform fee</span><span className="flex-1 pl-4">Partner pool — split between the two collaborating partners, on terms they agree</span></div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="pc-types">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHead title="One network. Multiple ways to collaborate." sub="Open to recruitment businesses across sectors, markets and geographies — pick the partnership that fits how you already work." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TYPES.map((t, i) => { const Icon = t.icon; return (
              <Reveal key={t.title} delay={(i % 3) * 80}>
                <div className="relative flex h-full flex-col rounded-2xl border border-border bg-white p-7 shadow-sm">
                  {t.tag && <span className="absolute right-5 top-5 rounded-full bg-gold px-2.5 py-0.5 text-[11px] font-semibold text-white">{t.tag}</span>}
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10 text-gold"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-serif text-lg text-navy">{t.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
                </div>
              </Reveal>
            ); })}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="scroll-mt-24 bg-white py-20 lg:py-24" data-testid="pc-how-it-works">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <SectionHead title="How a placement moves through the network" sub="From registering to settling the fee." />
          <ol className="relative mt-14 space-y-8 border-l border-border pl-8">
            {STEPS.map((s, i) => (
              <li key={s.t} className="relative">
                <span className="absolute -left-[45px] flex h-8 w-8 items-center justify-center rounded-full bg-gold text-xs font-bold text-white">{i + 1}</span>
                <h3 className="font-serif text-xl text-navy">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.b}</p>
                {s.note && <p className="mt-2 text-xs text-muted-foreground/80">{s.note}</p>}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="pc-trust">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHead title="Trust & verification" sub="A Verified Partner badge means something specific — here is exactly what it checks." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CHECKS.map((c) => { const Icon = c.icon; return (
              <div key={c.t} className="rounded-2xl border border-border bg-white p-6">
                <Icon className="h-6 w-6 text-gold" />
                <h3 className="mt-4 font-serif text-lg text-navy">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.b}</p>
              </div>
            ); })}
          </div>
          <p className="mx-auto mt-10 max-w-3xl text-center text-sm text-muted-foreground">Every application is reviewed by our team before a partner can share or view network activity. Status moves from Pending Verification to Verified Partner once checks are complete.</p>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24" data-testid="pc-faq">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <SectionHead title="Frequently asked questions" />
          <Accordion type="single" collapsible className="mt-10 w-full">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-navy">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="register" className="scroll-mt-24 bg-navy py-20 lg:py-24" data-testid="pc-register">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <SectionHead light title="Register your agency" sub="Open to limited companies, sole recruiters, and partnerships — across sectors, markets, and geographies." />
          <div className="mt-10 rounded-2xl border border-border bg-white p-6 sm:p-8"><PartnerApplicationForm /></div>
          <p className="mt-6 text-center text-xs text-white/50"><Link to="/partner-dashboard" className="text-gold-light hover:text-gold" data-testid="pc-partner-login-link">Sign in</Link></p>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-navy py-8 text-center text-xs text-white/50">© {new Date().getFullYear()} {COMPANY.name}. <Link to="/" className="hover:text-gold">Main website</Link> · <Link to="/legal" className="hover:text-gold">Policies</Link></footer>
    </div>
  );
}
